/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  Check as CheckIcon,
  Flag as ChallengeIcon,
  Comment as CommentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { ArrowLeft } from '../../../assets';
import PageArea from '../../../components/shared/PageArea';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import PageTitle from '../../../components/shared/PageTitle';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

import {
  Audit,
  AuditSection,
  AuditQuestion,
  AuditResponse,
} from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import { AuditStatus, QuestionType } from '../../../pages/Compliance/Enums/complianceEnums';
import { complianceService } from '../../../app/services/api/compliance/complianceService';
import { UserContext } from '../../../components/shared/useUser';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { Section } from '../../../components/ui/Section';
import { resizeImageForUpload } from '../../../utils/imageUtils';

interface ReviewComment {
  id: number;
  auditId: number;
  questionId?: number;
  userId: number;
  userName: string;
  comment: string;
  createdDate: Date;
  isChallenge: boolean;
}

interface ChallengeItem {
  questionId: number;
  questionText: string;
  currentResponse: string;
  reason: string;
  challengedOn?: string | null;
  challengedBy?: string | null;
}

const AuditReview: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('complianceAudits');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { user, hasPermissionTo, selectedStore } = useContext(UserContext);

  // Granular review capabilities — role-based action permissions
  const canComment = hasPermissionTo(['AuditReviewComment']);
  const canChallenge = hasPermissionTo(['AuditsChallenge']);
  const canAmend = hasPermissionTo(['AuditsExecute']);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [audit, setAudit] = useState<Audit | null>(null);
  const [sections, setSections] = useState<AuditSection[]>([]);
  const [responses, setResponses] = useState<Record<number, AuditResponse>>({});
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [challenges, setChallenges] = useState<ChallengeItem[]>([]);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentScore, setCurrentScore] = useState(0);
  const [maxScore, setMaxScore] = useState(0);

  // Dialogs
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  // Dialog state
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [newComment, setNewComment] = useState('');
  const [challengeReason, setChallengeReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [reviewerSignature, setReviewerSignature] = useState('');
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  // Edit mode — tracks which question is being edited
  const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);

  // Autosave state
  const [modifiedQuestions, setModifiedQuestions] = useState<Set<number>>(new Set());
  const [showAutosaveMessage, setShowAutosaveMessage] = useState<boolean>(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Refs to always read latest state inside debounced/async callbacks
  const responsesRef = useRef(responses);
  const auditRef = useRef(audit);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savingRef = useRef(false);

  useEffect(() => { responsesRef.current = responses; }, [responses]);
  useEffect(() => { auditRef.current = audit; }, [audit]);

  const AUTOSAVE_DEBOUNCE = 1500; // 1.5 seconds after last change

  const blocker = useBlocker(() => modifiedQuestions.size > 0);

  // Immediate save — reads from refs so it always has latest data
  const saveNow = useCallback(async () => {
    if (!auditRef.current || savingRef.current) return;
    savingRef.current = true;
    try {
      const responsesList = Object.values(responsesRef.current);
      await complianceService.audits.updateResponses(auditRef.current.id!, {
        storeNumber: selectedStore?.storeNumber,
        responses: responsesList,
      }, selectedStore?.storeNumber);
      setShowAutosaveMessage(true);
      setModifiedQuestions(new Set());
    } catch (err) {
      console.error('Autosave failed:', err);
    } finally {
      savingRef.current = false;
    }
  }, [selectedStore?.storeNumber]);

  // Debounced save — call this after every change; resets the 1.5s timer each time
  const debouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      saveNow();
    }, AUTOSAVE_DEBOUNCE);
  }, [saveNow]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const uploadToBlob = async (questionId: number, dataUrl: string): Promise<string> => {
    const resized = await resizeImageForUpload(dataUrl, true);
    const file = new File([resized], `signature-${Date.now()}.png`, { type: resized.type });
    const url = await complianceService.audits.uploadPhoto(Number(auditId), questionId, file);
    return url;
  };

  const handleAutosaveMessage = (changesSaved: boolean) => {
    setShowAutosaveMessage(changesSaved);
  };

  const handleEditToggle = (questionId: number) => {
    if (editingQuestionId === questionId) {
      // "Done Editing" — flush any pending debounce and save immediately
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (modifiedQuestions.size > 0) {
        saveNow();
      }
      setEditingQuestionId(null);
    } else {
      // Switching to a different question — save previous if modified
      if (editingQuestionId !== null && modifiedQuestions.size > 0) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        saveNow();
      }
      setEditingQuestionId(questionId);
    }
  };

  // Leave-page modal handlers
  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    saveNow();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  // Prevent browser close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: { preventDefault: () => void; returnValue: string }) => {
      if (modifiedQuestions.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [modifiedQuestions]);

  useEffect(() => {
    if (auditId) {
      loadAudit();
      loadComments();
    }
  }, [auditId, selectedStore?.storeNumber]);

  const loadAudit = async () => {
    try {
      setLoading(true);
      setError(null);

      const auditData = await complianceService.audits.getById(Number(auditId), selectedStore?.storeNumber);
      setAudit(auditData);

      // Use embedded template from audit response if available,
      // otherwise fall back to separate template API call (requires AuditTemplatesView permission)
      const templateData = auditData.template
        ?? (auditData.templateId && hasPermissionTo(['AuditTemplatesView'])
          ? await complianceService.auditTemplates.getById(auditData.templateId, selectedStore?.storeNumber)
          : null);

      const sectionsWithQuestions: AuditSection[] = (templateData?.sections || [])
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map(section => ({
          ...section,
          questions: [...(section.questions || [])].sort((a, b) => a.displayOrder - b.displayOrder),
        }));
      setSections(sectionsWithQuestions);

      // Load responses from the audit data
      const responsesData: AuditResponse[] = auditData.responses || [];
      const responsesMap: Record<number, AuditResponse> = {};
      responsesData.forEach((response: AuditResponse) => {
        responsesMap[response.questionId] = response;
      });
      setResponses(responsesMap);

      // Reconstruct challenges from response data
      const loadedChallenges: ChallengeItem[] = [];
      responsesData.forEach((response) => {
        if (response.isChallenged) {
          const question = sectionsWithQuestions
            .flatMap((s) => s.questions || [])
            .find((q) => q.id === response.questionId);
          loadedChallenges.push({
            questionId: response.questionId,
            questionText: question?.questionText || '',
            currentResponse: response.responseText || '',
            reason: response.challengeReason || '',
            challengedOn: response.challengedOn || null,
            challengedBy: response.challengedByUserName || null,
          });
        }
      });
      setChallenges(loadedChallenges);

      // Calculate scores
      const { current, max } = calculateTotalScore(sectionsWithQuestions, responsesMap);
      setCurrentScore(current);
      setMaxScore(max);

      // Reset autosave state after fresh load
      setModifiedQuestions(new Set());
      setShowAutosaveMessage(false);
    } catch (err) {
      console.error('Error loading audit:', err);
      setError(t('review.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      setComments([]);
    } catch (err) {
      console.error('Error loading comments:', err);
    }
  };

  const calculateTotalScore = (
    sectionsList: AuditSection[],
    responsesMap: Record<number, AuditResponse>,
  ): { current: number; max: number } => {
    let current = 0;
    let max = 0;

    sectionsList.forEach((section) => {
      section.questions?.forEach((question) => {
        max += question.maxScore || 0;
        const response = responsesMap[question.id];
        if (response) {
          current += response.score || 0;
        }
      });
    });

    return { current, max };
  };

  const handleAddComment = (questionId?: number) => {
    setSelectedQuestionId(questionId || null);
    setNewComment('');
    setCommentDialogOpen(true);
  };

  const handleSaveComment = async () => {
    if (!newComment.trim()) return;

    try {
      setSaving(true);
      setError(null);

      const newCommentObj: ReviewComment = {
        id: Date.now(),
        auditId: Number(auditId),
        questionId: selectedQuestionId || undefined,
        userId: 1,
        userName: user?.emailAddress || 'Current User',
        comment: newComment,
        createdDate: new Date(),
        isChallenge: false,
      };
      setComments([...comments, newCommentObj]);

      setSuccess(t('review.commentAdded'));
      setCommentDialogOpen(false);
      setNewComment('');
      setSelectedQuestionId(null);
    } catch (err) {
      console.error('Error saving comment:', err);
      setError(t('review.commentFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleChallengeQuestion = (question: AuditQuestion) => {
    setSelectedQuestionId(question.id);
    setChallengeReason('');
    setChallengeDialogOpen(true);
  };

  const handleSaveChallenge = async () => {
    if (!challengeReason.trim() || !selectedQuestionId) return;

    try {
      setSaving(true);
      setError(null);

      const question = sections
        .flatMap((s) => s.questions || [])
        .find((q) => q.id === selectedQuestionId);
      const response = responses[selectedQuestionId];

      if (!question || !response) return;

      // Call the backend challenge endpoint
      const updatedAudit = await complianceService.audits.challenge(
        Number(auditId),
        { comments: challengeReason.trim(), questionId: selectedQuestionId },
        selectedStore?.storeNumber,
      );

      // Update local audit state with the returned audit
      setAudit(updatedAudit);

      const challengeItem: ChallengeItem = {
        questionId: selectedQuestionId,
        questionText: question.questionText,
        currentResponse: response.responseText || '',
        reason: challengeReason,
        challengedOn: new Date().toISOString(),
        challengedBy: user?.emailAddress || null,
      };

      setChallenges([...challenges, challengeItem]);

      // Add as local comment for display
      const challengeComment: ReviewComment = {
        id: Date.now(),
        auditId: Number(auditId),
        questionId: selectedQuestionId,
        userId: 1,
        userName: user?.emailAddress || 'Current User',
        comment: `CHALLENGE: ${challengeReason}`,
        createdDate: new Date(),
        isChallenge: true,
      };
      setComments([...comments, challengeComment]);

      setSuccess(t('review.challengeSubmitted'));
      setChallengeDialogOpen(false);
      setChallengeReason('');
      setSelectedQuestionId(null);
    } catch (err) {
      console.error('Error saving challenge:', err);
      setError(t('review.challengeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = () => {
    setApprovalNotes('');
    setReviewerSignature('');
    setApproveDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!reviewerSignature) {
      setError(t('review.signatureRequiredToApprove'));
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await complianceService.audits.approve(Number(auditId), {
        storeNumber: selectedStore?.storeNumber,
        signatureData: reviewerSignature,
        approvalNotes: approvalNotes || null,
      }, selectedStore?.storeNumber);
      setSuccess(t('review.approveSuccess'));
      setTimeout(() => {
        navigate('/compliance/audits/history');
      }, 1500);
    } catch (err) {
      console.error('Error signing off audit:', err);
      setError(t('review.approveFailed'));
    } finally {
      setSaving(false);
      setApproveDialogOpen(false);
    }
  };

  const handleResponseChange = (question: AuditQuestion, value: string | number | boolean) => {
    const currentResponse = responses[question.id] || {
      id: 0,
      auditId: Number(auditId),
      questionId: question.id,
      responseText: null,
      responseNumber: null,
      responseBoolean: null,
      selectedOptionIds: [],
      photoUrls: [],
      signatureUrl: null,
      score: null,
    };

    const updatedResponse = { ...currentResponse };

    switch (question.questionType) {
      case QuestionType.Text:
      case QuestionType.Date:
        updatedResponse.responseText = String(value);
        updatedResponse.responseNumber = null;
        updatedResponse.responseBoolean = null;
        break;
      case QuestionType.Number:
        updatedResponse.responseNumber = Number(value);
        updatedResponse.responseText = null;
        updatedResponse.responseBoolean = null;
        break;
      case QuestionType.Boolean:
        updatedResponse.responseBoolean = Boolean(value);
        updatedResponse.responseText = null;
        updatedResponse.responseNumber = null;
        break;
      case QuestionType.SingleChoice:
        updatedResponse.selectedOptionIds = [Number(value)];
        updatedResponse.responseText = null;
        updatedResponse.responseNumber = null;
        updatedResponse.responseBoolean = null;
        break;
      case QuestionType.MultipleChoice: {
        const optionId = Number(value);
        const currentIds = updatedResponse.selectedOptionIds || [];
        const idx = currentIds.indexOf(optionId);
        if (idx > -1) {
          updatedResponse.selectedOptionIds = currentIds.filter(id => id !== optionId);
        } else {
          updatedResponse.selectedOptionIds = [...currentIds, optionId];
        }
        updatedResponse.responseText = null;
        updatedResponse.responseNumber = null;
        updatedResponse.responseBoolean = null;
        break;
      }
    }

    // Calculate score
    updatedResponse.score = calculateQuestionScore(question, updatedResponse);

    const newResponses = { ...responses, [question.id]: updatedResponse };
    setResponses(newResponses);

    // Track modified question, reset save message, and trigger debounced autosave
    setModifiedQuestions(prev => new Set(prev).add(question.id));
    handleAutosaveMessage(false);
    debouncedSave();

    // Recalculate total score
    const { current, max } = calculateTotalScore(sections, newResponses);
    setCurrentScore(current);
    setMaxScore(max);
  };

  const calculateQuestionScore = (question: AuditQuestion, response: AuditResponse): number => {
    switch (question.questionType) {
      case QuestionType.Boolean:
        return response.responseBoolean === true ? (question.maxScore || 0) : 0;

      case QuestionType.SingleChoice: {
        const optionId = response.selectedOptionIds?.[0];
        const option = question.options?.find(o => o.id === optionId);
        return option?.score || 0;
      }

      case QuestionType.MultipleChoice: {
        let totalScore = 0;
        response.selectedOptionIds?.forEach(optionId => {
          const option = question.options?.find(o => o.id === optionId);
          totalScore += option?.score || 0;
        });
        return Math.min(totalScore, question.maxScore || 0);
      }

      case QuestionType.Number:
      case QuestionType.Text:
      case QuestionType.Date:
      case QuestionType.Photo:
      case QuestionType.Signature:
        // For these types, award full points if answered
        return (response.responseText || response.responseNumber !== null ||
          response.photoUrls?.length || response.signatureUrl) ? (question.maxScore || 0) : 0;

      default:
        return 0;
    }
  };



  const handleDownloadPdf = async () => {
    if (!audit) return;
    try {
      await complianceService.audits.downloadPdf(audit.id, selectedStore?.storeNumber);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const handlePreviewPdf = async () => {
    if (!audit) return;
    try {
      await complianceService.audits.previewPdf(audit.id, selectedStore?.storeNumber);
    } catch (error) {
      console.error('Error previewing PDF:', error);
    }
  };

  const renderInlineForm = (question: AuditQuestion): JSX.Element => {
    const response = responses[question.id];

    switch (question.questionType) {
      case QuestionType.Text:
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={response?.responseText || ''}
            onChange={(e) => handleResponseChange(question, e.target.value)}
            placeholder={t('review.enterResponsePlaceholder')}
            size="small"
          />
        );

      case QuestionType.Number:
        return (
          <TextField
            fullWidth
            type="number"
            value={response?.responseNumber ?? ''}
            onChange={(e) => handleResponseChange(question, parseFloat(e.target.value))}
            placeholder={t('review.enterNumberPlaceholder')}
            size="small"
          />
        );

      case QuestionType.Boolean:
        return (
          <FormControl component="fieldset">
            <RadioGroup
              value={response?.responseBoolean === true ? 'true' : response?.responseBoolean === false ? 'false' : ''}
              onChange={(e) => handleResponseChange(question, e.target.value === 'true')}
            >
              <FormControlLabel value="true"
                control={<Radio />}
                label={t('common.yes')} />
              <FormControlLabel value="false"
                control={<Radio />}
                label={t('common.no')} />
            </RadioGroup>
          </FormControl>
        );

      case QuestionType.SingleChoice:
        return (
          <FormControl component="fieldset">
            <RadioGroup
              value={response?.selectedOptionIds?.[0] || ''}
              onChange={(e) => handleResponseChange(question, parseInt(e.target.value))}
            >
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio />}
                  label={`${option.optionText} (${option.score} pts)`}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case QuestionType.MultipleChoice:
        return (
          <FormControl component="fieldset">
            <FormGroup>
              {question.options?.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={
                    <Checkbox
                      checked={response?.selectedOptionIds?.includes(option.id) || false}
                      onChange={() => handleResponseChange(question, option.id)}
                    />
                  }
                  label={`${option.optionText} (${option.score} pts)`}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case QuestionType.Date:
        return (
          <DatePickerLocalized
            useCalendar={false}
            label={question.questionText || t('review.selectDate')}
            value={response?.responseText || ''}
            onChange={(newValue) => handleResponseChange(question, newValue || '')}
            translations={{
              clearButtonLabel: t('calendar.clearButtonLabel'),
              todayButtonLabel: t('calendar.todayButtonLabel'),
              cancelButtonLabel: t('calendar.cancelButtonLabel'),
              okButtonLabel: t('calendar.okButtonLabel'),
            }}
          />
        );

      case QuestionType.Photo:
      case QuestionType.Signature:
      case QuestionType.Rating:
        return (
          <Alert severity="info">
            {t('review.unsupportedEditMessage')}
          </Alert>
        );

      default:
        return <Box sx={{ color: 'error.main' }}>Unsupported question type</Box>;
    }
  };

  const getResponseValue = (question: AuditQuestion): string => {
    const response = responses[question.id];
    if (!response) return t('review.noResponse');

    switch (question.questionType) {
      case QuestionType.Boolean:
        return response.responseBoolean === true ? t('common.yes') : t('common.no');
      case QuestionType.SingleChoice: {
        const selectedId = response.selectedOptionIds?.[0];
        const option = question.options?.find(o => o.id === selectedId);
        return option ? option.optionText : (response.responseText || t('review.noResponse'));
      }
      case QuestionType.MultipleChoice: {
        const selectedOptions = (response.selectedOptionIds || [])
          .map(id => question.options?.find(o => o.id === id))
          .filter(Boolean)
          .map(o => o!.optionText);
        return selectedOptions.length > 0 ? selectedOptions.join(', ') : (response.responseText || t('review.noResponse'));
      }
      case QuestionType.Photo:
        return response.photoUrls && response.photoUrls.length > 0 ? t('review.photoAttached') : t('review.noPhoto');
      case QuestionType.Signature:
        return response.signatureUrl ? t('review.signatureAttached') : t('review.noSignature');
      default:
        return response.responseText || t('review.noResponse');
    }
  };

  const getQuestionComments = (questionId: number): ReviewComment[] => {
    return comments.filter((c) => c.questionId === questionId);
  };

  const getQuestionChallenge = (questionId: number): ChallengeItem | undefined => {
    return challenges.find((c) => c.questionId === questionId);
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  if (!audit) {
    return (
      <PageArea>
        <Alert severity="error">{t('review.auditNotFound')}</Alert>
      </PageArea>
    );
  }

  // Access guard: only users with AuditReviewView can access this page
  if (!hasPermissionTo(['AuditReviewView'])) {
    return (
      <PageArea>
        <Alert severity="warning">{t('common.noPermission')}</Alert>
      </PageArea>
    );
  }
  const localeLanguage = navigator.languages?.[0] || navigator.language;
  const currentSection = sections[currentSectionIndex];
  const scorePercentage = maxScore > 0 ? (currentScore / maxScore) * 100 : 0;

  return (
    <PageArea>
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <PageTitle>{t('review.pageTitle')}</PageTitle>
        </Stack>
        <Stack direction="row"
          spacing={2}>
          {audit.status === AuditStatus.Submitted && (
            <>
              <Button
                variant="secondary"
                size="lg"
                startIcon={<SvgIcon><VisibilityIcon /></SvgIcon>}
                onClick={handlePreviewPdf}
                disabled={saving}
              >
                {t('review.previewPdf')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon><DownloadIcon /></SvgIcon>}
                onClick={handleDownloadPdf}
                disabled={saving}
              >
                {t('common.download')} PDF
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {/* Autosave snackbar */}
      <MessagesSnackbar
        open={showAutosaveMessage}
        onClose={() => setShowAutosaveMessage(false)}
        message={t('execution.changesSaved')}
        severity="success"
        duration={3000}
      />

      {/* Alerts */}
      {error && (
        <Alert severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Audit Info Card */}
      <Section style={{ p: 5, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
          <Box>
            <Box sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 1 }}>
              {audit.templateName}
            </Box>
            <Box sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
              {t('review.storeAuditorLabel', { store: audit.storeName, auditor: audit.conductedByUserName || t('common.notApplicable') })}
            </Box>
            <Box sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
              {t('review.submittedLabel', { date: (audit.submittedOn || audit.completedDate) ? new Date((audit.submittedOn || audit.completedDate)!).toLocaleString(localeLanguage) : t('common.notApplicable') })}
            </Box>
          </Box>
          <Chip
            label={`${currentScore} / ${maxScore}`}
            color={currentScore === 0 ? 'error' : currentScore < maxScore ? 'warning' : 'success'}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {t('review.scoreLabel', { current: currentScore, max: maxScore, percent: scorePercentage.toFixed(1) })}
            </Box>
          </Box>
          <LinearProgress variant="determinate"
            value={scorePercentage}
            sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      </Section>

      {/* Stepper */}
      <Box sx={{ mt: 12, mb: 5 }}>
        <Stepper activeStep={currentSectionIndex}
          alternativeLabel>
          {sections.map((section) => (
            <Step key={section.id}>
              <StepLabel>{section.name}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Current Section */}
      {
        currentSection && (
          <Section style={{ p: 5, mb: 5 }}>
            <Box sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 2 }}>
              {currentSection.name}
            </Box>
            {currentSection.description && (
              <Box sx={{ fontSize: '1rem', color: theme.palette.text.secondary, mb: 3 }}>
                {currentSection.description}
              </Box>
            )}

            {/* Questions */}
            {currentSection.questions?.map((question, qIndex) => {
              const response = responses[question.id];
              const questionComments = getQuestionComments(question.id);
              const questionChallenge = getQuestionChallenge(question.id);

              return (
                <Section key={question.id}
                  style={{ p: 5, mb: 3, boxShadow: 'none' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
                        {qIndex + 1}. {question.questionText}
                        {question.isRequired && (
                          <Box component="span"
                            sx={{ color: theme.palette.error.main, ml: 0.5 }}>
                            *
                          </Box>
                        )}
                      </Box>
                      {question.helpText && (
                        <Box sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, mb: 2 }}>
                          {question.helpText}
                        </Box>
                      )}
                    </Box>
                    <Chip
                      label={question.maxScore ? `${response?.score || 0} / ${question.maxScore}` : 'N/A'}
                      color={response?.score === question.maxScore ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>

                  {/* Response — show form when editing, otherwise read-only text */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                      {t('review.response')}
                    </Box>
                    {editingQuestionId === question.id ? (
                      renderInlineForm(question)
                    ) : (
                      <Box sx={{ fontSize: '1rem' }}>{getResponseValue(question)}</Box>
                    )}
                  </Box>

                  {/* Response Photos */}
                  {response?.photoUrls && response.photoUrls.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                        {t('review.photosLabel')}
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                          gap: 2,
                        }}
                      >
                        {response.photoUrls.map((photoUrl, idx) => (
                          <Box
                            key={idx}
                            onClick={() => setLightboxUrl(photoUrl)}
                            sx={{
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              overflow: 'hidden',
                              height: 180,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            <img
                              src={photoUrl}
                              alt={t('execution.photoLabel', { index: idx + 1 })}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Response Signature */}
                  {response?.signatureUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                        {t('review.signatureLabel')}
                      </Box>
                      <Box
                        onClick={() => setLightboxUrl(response.signatureUrl!)}
                        sx={{
                          display: 'block',
                          width: 'fit-content',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                          p: 1,
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.85 },
                        }}
                      >
                        <img
                          src={response.signatureUrl}
                          alt={t('execution.signatureAlt')}
                          style={{ maxWidth: '100%', maxHeight: 150, display: 'block' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Action Trigger Data */}
                  {response?.actionNote && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                        {t('review.actionNoteLabel')}
                      </Box>
                      <Box sx={{ fontSize: '0.875rem', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        {response.actionNote}
                      </Box>
                    </Box>
                  )}

                  {(() => {
                    const legacyPhotos = response?.actionPhotoUrls ?? [];
                    const triggerPhotos = response?.actionTriggerPhotos?.flatMap(g => g.photoUrls) ?? [];
                    const combined = [...legacyPhotos, ...triggerPhotos];
                    const allActionPhotos = combined.filter((url, i) => combined.indexOf(url) === i);
                    return allActionPhotos.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                          {t('review.actionPhotosLabel')}
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: 2,
                          }}
                        >
                          {allActionPhotos.map((photoUrl, idx) => (
                            <Box
                              key={idx}
                              onClick={() => setLightboxUrl(photoUrl)}
                              sx={{
                                height: 180,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.85 },
                              }}
                            >
                              <img
                                src={photoUrl}
                                alt={t('execution.actionPhotoAlt', { index: idx + 1 })}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null;
                  })()}

                  {response?.actionSignatureUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
                        {t('review.actionSignatureLabel')}
                      </Box>
                      <Box
                        onClick={() => setLightboxUrl(response.actionSignatureUrl!)}
                        sx={{
                          display: 'inline-block',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                          p: 1,
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.85 },
                        }}
                      >
                        <img
                          src={response.actionSignatureUrl}
                          alt={t('execution.signatureAlt')}
                          style={{ maxWidth: '100%', maxHeight: 120, display: 'block' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Challenge Indicator */}
                  {questionChallenge && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'rgba(237, 108, 2, 0.08)',
                        border: '1px solid',
                        borderColor: 'warning.light',
                      }}
                    >
                      <Box sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'warning.dark' }}>
                        {t('review.challenged')}
                      </Box>
                      <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
                        {questionChallenge.reason}
                      </Box>
                      {(questionChallenge.challengedOn || questionChallenge.challengedBy) && (
                        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                          {questionChallenge.challengedBy && (
                            <>{t('review.challengedBy')}: {questionChallenge.challengedBy}</>
                          )}
                          {questionChallenge.challengedBy && questionChallenge.challengedOn && ' · '}
                          {questionChallenge.challengedOn && (
                            <>{new Date(questionChallenge.challengedOn).toLocaleString(localeLanguage)}</>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}



                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canAmend && audit && audit.status === AuditStatus.Submitted && (
                      <Button
                        variant="secondary"
                        size="lg"
                        startIcon={<SvgIcon>{editingQuestionId === question.id ? <CheckIcon /> : <EditIcon />}</SvgIcon>}
                        onClick={() => handleEditToggle(question.id)}
                        disabled={saving}
                      >
                        {editingQuestionId === question.id
                          ? t('review.saveChanges')
                          : t('review.editResponse')}
                      </Button>
                    )}
                    {canComment && audit && audit.status >= AuditStatus.Submitted && (
                      <Button
                        variant="secondary"
                        size="lg"
                        startIcon={<SvgIcon><CommentIcon /></SvgIcon>}
                        onClick={() => handleAddComment(question.id)}
                        disabled={saving}
                      >
                        {t('common.comment')}
                      </Button>
                    )}
                    {canChallenge && audit && audit.status === AuditStatus.Submitted && (
                      <Button
                        variant="secondary"
                        size="lg"
                        startIcon={<SvgIcon><ChallengeIcon /></SvgIcon>}
                        onClick={() => handleChallengeQuestion(question)}
                        disabled={!!questionChallenge || saving}
                      >
                        {t('review.challenge')}
                      </Button>
                    )}
                  </Box>
                </Section>
              );
            })}
          </Section>
        )}

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setCurrentSectionIndex(currentSectionIndex - 1)}
          disabled={currentSectionIndex === 0}
        >
          Previous Section
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => setCurrentSectionIndex(currentSectionIndex + 1)}
          disabled={currentSectionIndex === sections.length - 1}
        >
          Next Section
        </Button>
      </Box>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('review.addCommentDialog')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('review.commentLabel')}
            multiline
            rows={4}
            fullWidth
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setCommentDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary"
            onClick={handleSaveComment}
            disabled={!newComment.trim() || saving}>
            {t('review.addComment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Challenge Dialog */}
      <Dialog open={challengeDialogOpen}
        onClose={() => setChallengeDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('review.challengeResponseDialog')}</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary, mb: 2 }}>
            {t('review.challengeResponseMessage')}
          </Box>
          <TextField
            label={t('review.challengeReason')}
            multiline
            rows={4}
            fullWidth
            value={challengeReason}
            onChange={(e) => setChallengeReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => setChallengeDialogOpen(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSaveChallenge}
            disabled={!challengeReason.trim() || saving}
          >
            {t('review.submitChallenge')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reviewer Signature Capture Dialog */}
      <SignaturePadDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSave={async (signatureDataUrl) => {
          setSignatureDialogOpen(false);
          try {
            const uploadedUrl = await uploadToBlob(0, signatureDataUrl);
            setReviewerSignature(uploadedUrl);
          } catch {
            setReviewerSignature(signatureDataUrl);
          }
        }}
      />

      {/* Leave Page Modal — save prompt on navigation with unsaved changes */}
      <LeavePageModal
        open={blocker.state === 'blocked' && modifiedQuestions.size > 0}
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleLeavePageModalCancel}
        onConfirmUrl='/compliance/audits/history'
        leaveWarningContent={t('review.saveOnLeaveWarningContent')}
        leaveWarningTitle={t('review.saveOnLeaveWarningTitle')}
      />

      {/* Image Lightbox */}
      <Dialog
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: 1,
            maxHeight: '95vh',
            maxWidth: '95vw',
          },
        }}
      >
        {lightboxUrl && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <img
            src={lightboxUrl}
            alt=""
            onClick={() => setLightboxUrl(null)}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              display: 'block',
              cursor: 'pointer',
              borderRadius: 8,
            }}
          />
        )}
      </Dialog>
    </PageArea>
  );
};

// Signature Pad Dialog Component
interface SignaturePadDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (_signatureDataUrl: string) => void;
}

const SignaturePadDialog: React.FC<SignaturePadDialogProps> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [isEmpty, setIsEmpty] = React.useState(true);
  const { t } = useTranslation('complianceAudits');

  React.useEffect(() => {
    if (open && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
      setIsEmpty(true);
    }
  }, [open]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0];
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      };
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    }
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return;

    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t('review.captureSignatureDialog')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, fontSize: '14px', color: theme.palette.text.secondary }}>
          {t('review.captureSignatureInstructions')}
        </Box>
        <Box
          sx={{
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            touchAction: 'none',
            cursor: 'crosshair',
          }}
        >
          <canvas
            ref={canvasRef}
            width={600}
            height={300}
            style={{
              display: 'block',
              width: '100%',
              height: 'auto',
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={clearSignature}
        >
          {t('review.clear')}
        </Button>
        <Button
          variant="secondary"
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={saveSignature}
          disabled={isEmpty}
        >
          {t('review.saveSignature')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditReview;
